import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { adApi } from '../../api/ad.api';
import { rewardApi } from '../../api/reward.api';
import { UserChangeItem } from '../../types/Admin.api.type';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';

type TabType = 'changes' | 'ads' | 'rewards' | 'exchange' | 'login';

interface UserDetailProps {
  userId: number;
  onBack: () => void;
}

/** 변경 주체 한국어 매핑 */
const ACTOR_LABELS: Record<string, string> = {
  SELF: '본인',
  ADMIN: '관리자',
  SYSTEM: '시스템',
};

/** 필드명 한국어 매핑 */
const FIELD_LABELS: Record<string, string> = {
  nickname: '닉네임',
  gender: '성별',
  birthdate: '생년월일',
  profileImageUrl: '프로필 이미지',
};

/** 날짜 포맷 */
const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const UserDetail = ({ userId, onBack }: UserDetailProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('changes');
  const [changePage, setChangePage] = useState(0);
  const [adPage, setAdPage] = useState(0);
  const [rewardPage, setRewardPage] = useState(0);
  const [exchangePage, setExchangePage] = useState(0);
  const [loginPage, setLoginPage] = useState(0);
  const pageSize = 20;

  /** 유저 상세 정보 */
  const { data: userDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['userDetail', userId],
    queryFn: () => adminApi.getUserDetail(userId).then(res => res.data),
  });

  /** 변경이력 */
  const { data: changesData, isLoading: changesLoading } = useQuery({
    queryKey: ['userChanges', userId, changePage],
    queryFn: () => adminApi.getUserChanges(userId, changePage, pageSize).then(res => res.data),
    enabled: activeTab === 'changes',
  });

  /** 광고 */
  const { data: adData, isLoading: adLoading } = useQuery({
    queryKey: ['adWatchHistory', userId, adPage],
    queryFn: () => adApi.getWatchHistory({ userId, page: adPage, size: pageSize }).then(res => res.data),
    enabled: activeTab === 'ads',
  });

  /** 광고 위치별 집계 (전체 데이터) */
  const { data: adAllData } = useQuery({
    queryKey: ['adWatchHistoryAll', userId],
    queryFn: () => adApi.getWatchHistory({ userId, size: 9999 }).then(res => res.data),
    enabled: activeTab === 'ads',
  });

  const adLocationSummary = (() => {
    if (!adAllData?.content) return [];
    const countMap: Record<string, { label: string; count: number }> = {};
    adAllData.content.forEach((item: any) => {
      const key = item.watchLocation || 'UNKNOWN';
      if (!countMap[key]) {
        countMap[key] = { label: item.watchLocationLabel || key, count: 0 };
      }
      countMap[key].count++;
    });
    return Object.values(countMap).sort((a, b) => b.count - a.count);
  })();

  /** 리워드 */
  const { data: rewardData, isLoading: rewardLoading } = useQuery({
    queryKey: ['adminRewardHistory', userId, rewardPage],
    queryFn: () => rewardApi.getHistory(rewardPage, pageSize, userId).then(res => res.data.results),
    enabled: activeTab === 'rewards',
  });

  /** 환전 */
  const { data: exchangeData, isLoading: exchangeLoading } = useQuery({
    queryKey: ['adminExchangeHistory', userId, exchangePage],
    queryFn: () => rewardApi.getExchangeHistory(exchangePage, pageSize, userId).then(res => res.data.results),
    enabled: activeTab === 'exchange',
  });

  /** 로그인 */
  const { data: loginData, isLoading: loginLoading } = useQuery({
    queryKey: ['loginHistories', userId, loginPage],
    queryFn: () => adminApi.getLoginHistories(undefined, undefined, loginPage, pageSize, userId).then(res => res.data),
    enabled: activeTab === 'login',
  });

  const tabs: { key: TabType; label: string }[] = [
    { key: 'changes', label: '변경이력' },
    { key: 'ads', label: '광고' },
    { key: 'rewards', label: '리워드' },
    { key: 'exchange', label: '환전' },
    { key: 'login', label: '로그인' },
  ];

  /** 페이지네이션 컴포넌트 */
  const Pagination = ({ page, totalPages, totalElements, onPrev, onNext }: {
    page: number;
    totalPages: number;
    totalElements: number;
    onPrev: () => void;
    onNext: () => void;
  }) => (
    <div className="flex items-center justify-between mt-4 pt-3 border-t">
      <span className="text-xs text-gray-500">총 {totalElements}건</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page === 0} onClick={onPrev}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm text-gray-600">{page + 1} / {totalPages || 1}</span>
        <Button variant="outline" size="sm" disabled={page >= (totalPages || 1) - 1} onClick={onNext}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            목록으로
          </Button>
        </div>

        {detailLoading ? (
          <div className="text-center py-4 text-gray-400">로딩 중...</div>
        ) : userDetail ? (
          <div className="flex flex-col md:flex-row gap-6">
            {/* 프로필 */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                {userDetail.profileImageUrl ? (
                  <img src={userDetail.profileImageUrl} alt={userDetail.nickname} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <span className="text-2xl text-gray-400 font-medium">
                    {userDetail.nickname?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900">{userDetail.nickname}</h2>
                  <Badge variant="outline" className="text-xs">{userDetail.role}</Badge>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    userDetail.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {userDetail.status === 'ACTIVE' ? '활성' : '비활성'}
                  </span>
                  {userDetail.isBot && <Badge className="bg-purple-100 text-purple-700 text-xs">BOT</Badge>}
                </div>
                <p className="text-sm text-gray-500">{userDetail.email || '-'}</p>
                <p className="text-xs text-gray-400">
                  {userDetail.socialPlatform} | 가입: {formatDate(userDetail.regDateTime)} | 최종 로그인: {formatDate(userDetail.lastLoginAt)}
                </p>
              </div>
            </div>

            {/* 포인트 / 추가정보 */}
            <div className="flex gap-4 md:ml-auto">
              <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">잔액</p>
                <p className="text-lg font-bold text-gray-900">{userDetail.point.balance.toLocaleString()}</p>
              </div>
              <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">총 적립</p>
                <p className="text-lg font-bold text-green-600">{userDetail.point.totalEarned.toLocaleString()}</p>
              </div>
              <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">총 환전</p>
                <p className="text-lg font-bold text-blue-600">{userDetail.point.totalExchanged.toLocaleString()}</p>
              </div>
              {userDetail.gender && (
                <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">성별</p>
                  <p className="text-sm font-medium">{userDetail.gender}</p>
                </div>
              )}
              {userDetail.birthdate && (
                <div className="text-center px-4 py-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">생년월일</p>
                  <p className="text-sm font-medium">{userDetail.birthdate}</p>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* 서브탭 */}
      <div className="flex gap-2">
        {tabs.map(tab => (
          <Button
            key={tab.key}
            variant={activeTab === tab.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* 탭 컨텐츠 */}
      <Card>
        <div className="p-6">
          {/* 변경이력 탭 */}
          {activeTab === 'changes' && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">정보 변경 이력</h3>
              {changesLoading ? (
                <div className="text-center py-8 text-gray-400">로딩 중...</div>
              ) : !changesData?.content?.length ? (
                <div className="text-center py-8 text-gray-400">변경 이력이 없습니다.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">필드</th>
                          <th className="pb-2 pr-3">이전 값</th>
                          <th className="pb-2 pr-3">변경 값</th>
                          <th className="pb-2 pr-3">변경 주체</th>
                          <th className="pb-2">날짜</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changesData.content.map((item: UserChangeItem) => (
                          <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-2 pr-3 font-medium">{FIELD_LABELS[item.fieldName] || item.fieldName}</td>
                            <td className="py-2 pr-3 text-gray-500 max-w-[200px] truncate" title={item.oldValue ?? undefined}>
                              {item.oldValue || <span className="text-gray-300">없음</span>}
                            </td>
                            <td className="py-2 pr-3 max-w-[200px] truncate" title={item.newValue ?? undefined}>
                              {item.newValue || <span className="text-gray-300">없음</span>}
                            </td>
                            <td className="py-2 pr-3">
                              <Badge variant="outline" className="text-xs">
                                {ACTOR_LABELS[item.changedBy] || item.changedBy}
                              </Badge>
                            </td>
                            <td className="py-2 text-gray-400 text-xs">{formatDate(item.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={changePage}
                    totalPages={changesData.totalPages}
                    totalElements={changesData.totalElements}
                    onPrev={() => setChangePage(p => p - 1)}
                    onNext={() => setChangePage(p => p + 1)}
                  />
                </>
              )}
            </>
          )}

          {/* 광고 탭 */}
          {activeTab === 'ads' && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">광고 시청 이력</h3>
              {adLocationSummary.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-4">
                  {adLocationSummary.map(item => (
                    <div key={item.label} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">{item.label}</span>
                      <span className="text-sm font-bold text-gray-900">{item.count}건</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                    <span className="text-sm text-blue-600 font-medium">합계</span>
                    <span className="text-sm font-bold text-blue-700">{adLocationSummary.reduce((s, i) => s + i.count, 0)}건</span>
                  </div>
                </div>
              )}
              {adLoading ? (
                <div className="text-center py-8 text-gray-400">로딩 중...</div>
              ) : !adData?.content?.length ? (
                <div className="text-center py-8 text-gray-400">광고 시청 이력이 없습니다.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">ID</th>
                          <th className="pb-2 pr-3">위치</th>
                          <th className="pb-2 pr-3">플랫폼</th>
                          <th className="pb-2 pr-3">참조ID</th>
                          <th className="pb-2">날짜</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adData.content.map((item: any) => (
                          <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-2 pr-3 text-gray-400">{item.id}</td>
                            <td className="py-2 pr-3">{item.watchLocationLabel || item.watchLocation}</td>
                            <td className="py-2 pr-3">{item.platform || '-'}</td>
                            <td className="py-2 pr-3">{item.referenceId ? `#${item.referenceId}` : '-'}</td>
                            <td className="py-2 text-gray-400 text-xs">{formatDate(item.regDateTime)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={adPage}
                    totalPages={adData.totalPages}
                    totalElements={adData.totalElements}
                    onPrev={() => setAdPage(p => p - 1)}
                    onNext={() => setAdPage(p => p + 1)}
                  />
                </>
              )}
            </>
          )}

          {/* 리워드 탭 */}
          {activeTab === 'rewards' && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">리워드 지급 이력</h3>
              {rewardLoading ? (
                <div className="text-center py-8 text-gray-400">로딩 중...</div>
              ) : !rewardData?.content?.length ? (
                <div className="text-center py-8 text-gray-400">리워드 이력이 없습니다.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">ID</th>
                          <th className="pb-2 pr-3">종류</th>
                          <th className="pb-2 pr-3">금액</th>
                          <th className="pb-2 pr-3">상태</th>
                          <th className="pb-2">날짜</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rewardData.content.map((item: any) => (
                          <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-2 pr-3 text-gray-400">{item.id}</td>
                            <td className="py-2 pr-3">{item.rewardType}</td>
                            <td className="py-2 pr-3 font-medium">{item.amount}</td>
                            <td className="py-2 pr-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                item.status === 'SUCCESS' ? 'bg-green-100 text-green-700' :
                                item.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {item.status}
                              </span>
                            </td>
                            <td className="py-2 text-gray-400 text-xs">{formatDate(item.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={rewardPage}
                    totalPages={rewardData.totalPages}
                    totalElements={rewardData.totalElements}
                    onPrev={() => setRewardPage(p => p - 1)}
                    onNext={() => setRewardPage(p => p + 1)}
                  />
                </>
              )}
            </>
          )}

          {/* 환전 탭 */}
          {activeTab === 'exchange' && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">환전(토스포인트 지급) 이력</h3>
              {exchangeLoading ? (
                <div className="text-center py-8 text-gray-400">로딩 중...</div>
              ) : !exchangeData?.content?.length ? (
                <div className="text-center py-8 text-gray-400">환전 이력이 없습니다.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">ID</th>
                          <th className="pb-2 pr-3">조각</th>
                          <th className="pb-2 pr-3">토스포인트</th>
                          <th className="pb-2 pr-3">광고</th>
                          <th className="pb-2 pr-3">상태</th>
                          <th className="pb-2">날짜</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exchangeData.content.map((item: any) => (
                          <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-2 pr-3 text-gray-400">{item.id}</td>
                            <td className="py-2 pr-3 font-medium">{item.pointAmount}개</td>
                            <td className="py-2 pr-3 font-medium text-blue-600">{item.tossAmount?.toLocaleString()}원</td>
                            <td className="py-2 pr-3">
                              {item.adWatched ? (
                                <Badge variant="outline" className="text-xs text-green-600">시청</Badge>
                              ) : '-'}
                            </td>
                            <td className="py-2 pr-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                item.status === 'SUCCESS' ? 'bg-green-100 text-green-700' :
                                item.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {item.status}
                              </span>
                            </td>
                            <td className="py-2 text-gray-400 text-xs">{formatDate(item.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={exchangePage}
                    totalPages={exchangeData.totalPages}
                    totalElements={exchangeData.totalElements}
                    onPrev={() => setExchangePage(p => p - 1)}
                    onNext={() => setExchangePage(p => p + 1)}
                  />
                </>
              )}
            </>
          )}

          {/* 로그인 탭 */}
          {activeTab === 'login' && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">로그인 이력</h3>
              {loginLoading ? (
                <div className="text-center py-8 text-gray-400">로딩 중...</div>
              ) : !loginData?.content?.length ? (
                <div className="text-center py-8 text-gray-400">로그인 이력이 없습니다.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">플랫폼</th>
                          <th className="pb-2 pr-3">IP</th>
                          <th className="pb-2 pr-3">기기</th>
                          <th className="pb-2 pr-3">브라우저</th>
                          <th className="pb-2 pr-3">OS</th>
                          <th className="pb-2">시간</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginData.content.map((item: any) => (
                          <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-2 pr-3">
                              <Badge variant="outline" className="text-xs">{item.platform}</Badge>
                            </td>
                            <td className="py-2 pr-3 text-gray-500">{item.ipAddress}</td>
                            <td className="py-2 pr-3">{item.deviceTypeDescription || item.deviceType}</td>
                            <td className="py-2 pr-3">{item.browser || '-'}</td>
                            <td className="py-2 pr-3">{item.os || '-'}</td>
                            <td className="py-2 text-gray-400 text-xs">{formatDate(item.loginTime)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={loginPage}
                    totalPages={loginData.totalPages}
                    totalElements={loginData.totalElements}
                    onPrev={() => setLoginPage(p => p - 1)}
                    onNext={() => setLoginPage(p => p + 1)}
                  />
                </>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
